<template>
    <v-card>
        <v-card-title>
            <v-btn-toggle v-model="statusFilter" mandatory dense class="mr-4">
                <v-btn value="all" small>{{ $t('processes.filterAll') }}</v-btn>
                <v-btn value="Running" small color="info" outlined>{{ $t('processes.filterRunning') }}</v-btn>
                <v-btn value="Completed" small color="success" outlined>{{ $t('processes.filterCompleted') }}</v-btn>
                <v-btn value="Failed" small color="error" outlined>{{ $t('processes.filterFailed') }}</v-btn>
            </v-btn-toggle>
            <v-spacer></v-spacer>
            <v-btn icon :loading="loading" @click="refresh" :title="$t('processes.refresh')">
                <v-icon>mdi-refresh</v-icon>
            </v-btn>
        </v-card-title>

        <v-alert v-if="error" type="error" dense class="mx-4">{{ error }}</v-alert>

        <v-data-table
            :headers="headers"
            :items="filteredRuns"
            :loading="loading"
            :items-per-page="50"
            :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
            sort-by="startedAt"
            :sort-desc="true"
            show-expand
            item-key="processRunId"
            dense
        >
            <template #item.status="{ item }">
                <v-chip small :color="statusColor(item.status)" dark>{{ item.status }}</v-chip>
            </template>

            <template #item.triggerType="{ item }">
                <v-chip small color="grey lighten-2" class="caption">{{ item.triggerType }}</v-chip>
            </template>

            <template #item.startedAt="{ item }">
                {{ formatDate(item.startedAt) }}
            </template>

            <template #item.durationSeconds="{ item }">
                {{ item.durationSeconds != null ? item.durationSeconds.toFixed(1) + 's' : '—' }}
            </template>

            <template #item.errorMessage="{ item }">
                <v-tooltip v-if="item.errorMessage" bottom max-width="400">
                    <template #activator="{ on }">
                        <span v-on="on" class="error--text" style="cursor:pointer">
                            {{ item.errorMessage | truncate(60) }}
                        </span>
                    </template>
                    <span>{{ item.errorMessage }}</span>
                </v-tooltip>
                <span v-else>—</span>
            </template>

            <template #expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-3">
                    <pre v-if="item.metadataJson" class="caption" style="white-space:pre-wrap">{{ prettyJson(item.metadataJson) }}</pre>
                    <span v-else class="grey--text caption">{{ $t('processes.noMetadata') }}</span>
                </td>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
import { mapGetters } from "vuex";

export default {
    name: "ProcessRunTable",

    filters: {
        truncate(value, length) {
            if (!value) return "";
            return value.length > length ? value.substring(0, length) + "…" : value;
        }
    },

    data() {
        return {
            statusFilter: "all",
            refreshTimer: null,
            headers: [
                { text: this.$t("processes.colTenant"), value: "tenantId", width: "110px" },
                { text: this.$t("processes.colTitle"), value: "title" },
                { text: this.$t("processes.colTriggerType"), value: "triggerType", width: "140px" },
                { text: this.$t("processes.colStatus"), value: "status", width: "120px" },
                { text: this.$t("processes.colStartedAt"), value: "startedAt", width: "160px" },
                { text: this.$t("processes.colDuration"), value: "durationSeconds", width: "90px" },
                { text: this.$t("processes.colError"), value: "errorMessage" },
                { text: "", value: "data-table-expand", width: "40px" }
            ]
        };
    },

    computed: {
        ...mapGetters("processRun", ["runs", "loading", "error"]),
        filteredRuns() {
            if (this.statusFilter === "all") return this.runs;
            return this.runs.filter(r => r.status === this.statusFilter);
        }
    },

    methods: {
        refresh() {
            this.$store.dispatch("processRun/fetchRecentRuns");
        },
        statusColor(status) {
            if (status === "Running") return "info";
            if (status === "Completed") return "success";
            if (status === "Failed") return "error";
            return "grey";
        },
        formatDate(value) {
            if (!value) return "—";
            const d = new Date(value);
            return d.toLocaleDateString("fi-FI") + " " + d.toLocaleTimeString("fi-FI");
        },
        prettyJson(json) {
            try {
                return JSON.stringify(JSON.parse(json), null, 2);
            } catch {
                return json;
            }
        }
    },

    mounted() {
        this.refresh();
        this.refreshTimer = setInterval(this.refresh, 30000);
    },

    beforeDestroy() {
        clearInterval(this.refreshTimer);
    }
};
</script>
